import { type ComponentPropsWithoutRef, useState } from "react";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { getReportAccessProblemLinks } from "shared/components/report-access/reportAccessLinks";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useSocietySession } from "shared/context/SocietySessionContext";
import {
  type ReportAccessProblem,
  ReportAccessProblemSeverity,
} from "shared/report-access/reportAccessTypes";

const severityLabel: Record<ReportAccessProblemSeverity, string> = {
  [ReportAccessProblemSeverity.error]: "Error",
  [ReportAccessProblemSeverity.warning]: "Warning",
  [ReportAccessProblemSeverity.info]: "Info",
};

const severityClassName: Record<ReportAccessProblemSeverity, string> = {
  [ReportAccessProblemSeverity.error]: "bg-status-danger text-neutral-dark",
  [ReportAccessProblemSeverity.warning]:
    "bg-status-warning-tint text-neutral-dark",
  [ReportAccessProblemSeverity.info]: "bg-neutral-light-600 text-neutral-dark",
};

const severities = [
  ReportAccessProblemSeverity.error,
  ReportAccessProblemSeverity.warning,
  ReportAccessProblemSeverity.info,
];

type ReportAccessProblemsPanelProps = {
  /** Problems to list, most severe first */
  problems: ReportAccessProblem[];
  /** Panel heading */
  title: string;
  /** Shown when there are no problems; nothing renders when omitted */
  emptyMessage?: string;
  /** Start expanded */
  defaultOpen?: boolean;
  /** data-testid prefix */
  testid: string;
} & ComponentPropsWithoutRef<"div">;

/**
 * Report access misconfigurations, grouped by severity, each with buttons to
 * the pages where staff fix them. Errors are what PMGD-1763 will break or
 * block once it enforces report access.
 */
const ReportAccessProblemsPanel = ({
  problems,
  title,
  emptyMessage,
  defaultOpen = false,
  testid,
  className,
  ...props
}: ReportAccessProblemsPanelProps) => {
  const router = useCustomRouter();
  const { societyId } = useSocietySession();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (problems.length === 0) {
    return emptyMessage ? (
      <div
        className={`rounded border border-neutral-light-600 p-1.5 text-sm ${className ?? ""}`}
        data-testid={`${testid}-empty`}
        {...props}
      >
        {emptyMessage}
      </div>
    ) : null;
  }

  const counts = severities
    .map((severity) => ({
      severity,
      total: problems.filter((problem) => problem.severity === severity).length,
    }))
    .filter(({ total }) => total > 0);
  const hasErrors = counts.some(
    ({ severity }) => severity === ReportAccessProblemSeverity.error,
  );

  return (
    <div
      className={`rounded border p-1.5 ${hasErrors ? "border-status-danger" : "border-status-warning"} ${className ?? ""}`}
      data-testid={testid}
      {...props}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setIsOpen((open) => !open)}
        data-testid={`${testid}-toggle`}
        aria-expanded={isOpen}
      >
        <span className="font-semibold">{title}</span>
        <span className="flex items-center gap-1 text-sm">
          {counts.map(({ severity, total }) => (
            <span
              key={severity}
              className={`rounded px-1 py-0.5 ${severityClassName[severity]}`}
              data-testid={`${testid}-count-${severity}`}
            >
              {total} {severityLabel[severity]}
              {total === 1 ? "" : "s"}
            </span>
          ))}
          <span>{isOpen ? "Hide" : "Show"}</span>
        </span>
      </button>
      {isOpen && (
        <ul className="mt-1.5 flex max-h-96 flex-col gap-1 overflow-y-auto">
          {problems.map((problem, index) => (
            <li
              key={`${problem.code}-${problem.customReportId}-${index}`}
              className="flex flex-col gap-0.5 border-t border-neutral-light-600 pt-1 text-sm"
              data-testid={`${testid}-problem-${problem.code}`}
            >
              <div className="flex items-start gap-1">
                <span
                  className={`shrink-0 rounded px-1 py-0.5 text-xs ${severityClassName[problem.severity]}`}
                >
                  {severityLabel[problem.severity]}
                </span>
                <span>{problem.message}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {getReportAccessProblemLinks({ societyId, problem }).map(
                  (link) => (
                    <PillarForm.Button
                      key={link.key}
                      label={link.label}
                      className="button-small-neutral-filled"
                      testid={`${testid}-link-${link.key}`}
                      onClick={() => router.push(link.href)}
                    />
                  ),
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ReportAccessProblemsPanel;
